import React, { useEffect, useMemo, useState } from "react";
import ISO6391 from "iso-639-1";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BillingPageState } from "@/components/pages/billing-settings/components/BillingPageState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RichEditor,
  getEmptyRichEditorData,
  renderRichEditorContent,
} from "@/features/rich-editor";
import {
  isLegalPageMissingError,
  useCreateLegalPage,
  useLegalPage,
  useUpdateLegalPage,
} from "@/hooks/useLegalPages";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { LegalPageType } from "@/types/legal-page";
import {
  normalizeLegalPageStructure,
  parseLegalPageHtmlToRichEditorData,
} from "./legal-page.utils";

interface LegalPageEditorProps {
  type: LegalPageType;
}

export const LegalPageEditor: React.FC<LegalPageEditorProps> = ({ type }) => {
  const { t, i18n } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("settings");
  const defaultLanguage = (i18n.resolvedLanguage || i18n.language || "en")
    .split("-")[0]
    .toLowerCase();
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const localizedKey = `${type}-${selectedLanguage}`;
  const supportedLanguages = useMemo(
    () =>
      ISO6391.getAllCodes()
        .map((language) => ({
          value: language,
          label: ISO6391.getNativeName(language) || ISO6391.getName(language),
        }))
        .filter((language) => Boolean(language.label))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [],
  );
  const {
    data: legalPage,
    isLoading,
    isError,
    error,
  } = useLegalPage(type, selectedLanguage);
  const createLegalPage = useCreateLegalPage();
  const updateLegalPage = useUpdateLegalPage();
  const [content, setContent] = useState("");
  const [contentData, setContentData] = useState(getEmptyRichEditorData());
  const isMissingPage = isLegalPageMissingError(error);

  useEffect(() => {
    if (legalPage) {
      const normalizedStructure = normalizeLegalPageStructure(
        legalPage.structure,
      );

      setContent(legalPage.content);
      setContentData(
        normalizedStructure
          ? normalizedStructure
          : parseLegalPageHtmlToRichEditorData(legalPage.content),
      );
      return;
    }

    if (isMissingPage) {
      setContent("");
      setContentData(getEmptyRichEditorData());
    }
  }, [isMissingPage, legalPage]);

  const formattedUpdatedAt = useMemo(() => {
    if (!legalPage?.updatedAt) {
      return "";
    }

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(legalPage.updatedAt));
  }, [legalPage?.updatedAt]);

  const isDirty = (legalPage?.content ?? "") !== content;
  const isSaving = createLegalPage.isPending || updateLegalPage.isPending;

  const handleSave = async () => {
    try {
      const data = {
        content,
        key: localizedKey,
        structure: contentData,
      };

      if (legalPage) {
        await updateLegalPage.mutateAsync({
          key: localizedKey,
          language: selectedLanguage,
          data: {
            content,
            structure: contentData,
          },
        });
      } else {
        await createLegalPage.mutateAsync(data);
      }

      toast.success(t("layout.legalPages.toast.success"));
    } catch {
      // BaseService already reports API errors through Sonner.
    }
  };

  if (isLoading) {
    return <BillingPageState text={t("layout.legalPages.loading")} />;
  }

  if (isError && !isMissingPage) {
    return (
      <BillingPageState text={t("layout.legalPages.loadError")} tone="error" />
    );
  }

  return (
    <div className="flex w-full h-full gap-8 p-8 items-start">
      <Card className="mx-auto w-full rounded-3xl border-zinc-200 py-0">
        <CardContent className="min-h-[520px] px-5 py-4">
          <RichEditor
            value={contentData}
            placeholder={t("layout.legalPages.editorPlaceholder")}
            onChange={(data) => {
              setContentData(data);
              setContent(
                renderRichEditorContent(data, "", {
                  wrapInRoot: false,
                }),
              );
            }}
          />
        </CardContent>
      </Card>

      <aside className="h-fit self-start sticky top-24 flex-1">
        <div className="flex flex-col gap-3">
          <Card className="rounded-3xl border-zinc-200 gap-0">
            <CardHeader className="px-5 py-4">
              <CardTitle className="text-base text-zinc-900">
                {t("layout.legalPages.languageTitle")}
              </CardTitle>
              <CardDescription className="text-sm text-zinc-500">
                {t("layout.legalPages.languageDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-5 pb-5 pt-0">
              <Select
                value={selectedLanguage}
                onValueChange={(value) => setSelectedLanguage(value)}
              >
                <SelectTrigger className="h-11 w-full justify-between rounded-xl border-zinc-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map((language) => (
                    <SelectItem key={language.value} value={language.value}>
                      {language.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-zinc-200 gap-0">
            <CardHeader className="px-5 py-4">
              <CardTitle className="text-base text-zinc-900">
                {t("layout.legalPages.statusTitle")}
              </CardTitle>
              <CardDescription className="text-sm text-zinc-500">
                {formattedUpdatedAt
                  ? t("layout.legalPages.lastUpdated", {
                      value: formattedUpdatedAt,
                    })
                  : t("layout.legalPages.notSavedYet")}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-5 pb-0 pt-0"></CardContent>
            <CardFooter className="justify-end gap-2 px-5 py-4">
              <Button
                variant="outline"
                className="rounded-xl border-zinc-200 bg-white"
                onClick={() => {
                  window.open(`/${type}`, "_blank");
                }}
              >
                {t("layout.legalPages.preview")}
              </Button>
              <Button
                type="button"
                className="min-w-[180px] rounded-xl bg-zinc-900 text-white"
                disabled={!canWrite || !isDirty || isSaving}
                onClick={handleSave}
                title={
                  !canWrite
                    ? t(
                        "common.insufficientPermissions",
                        "Insufficient permissions",
                      )
                    : undefined
                }
              >
                {isSaving
                  ? t("layout.legalPages.saving")
                  : t("layout.legalPages.save")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </aside>
    </div>
  );
};
